import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSub } from '../../../helpers/hooks/pubSub';
import { getEnvValueFor } from '../../../helpers/utils';
import usePusher from '../../../helpers/hooks/usePusher';
import { createConsumer, Consumer, Subscription } from '@rails/actioncable';
import { MessageVote, RequestStatus, SenderRole } from '../helpers/enums';
import { useChatEventHandler } from '../hooks/useChatEventHandler';
import { assistantApi } from '../services/assistant';
import { sortMessageBlocks } from '../theme/utils';
import {
  type AssistantMessage,
  type Conversation,
  type Message,
  type TokenUsageStats,
  type UserMessage
} from '../types';
import { DashboardEventsContext } from '../../../components/dashboardEvents';

// Context interface
export interface AskVantageContextType {
  // Sidebar
  sidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;

  // Messages and conversation
  messages: Message[];
  send: (input: string, opts?: { contextIds?: string[] }) => Promise<void>;
  // Message box interaction
  fillMessageBox: (text: string) => void;
  registerFillMessageBox: (fn: (text: string) => void) => void;
  // Auto-send setting
  autoSendEnabled: boolean;
  toggleAutoSend: () => void;

  // Quota
  quota: {
    display_text: string;
    current_usage: TokenUsageStats;
    loading: boolean;
    error: string | null;
  };
  refreshQuota: () => Promise<void>;
  isQuotaExceeded: boolean;

  // Configuration
  kpiBarVisible: boolean;

  // Conversation management
  conversations: Conversation[];
  activeConversationId: number | string | null;
  createConversation: () => void;
  selectConversation: (id: string) => void;
  renameConversation: (id: number | string, title: string) => void;
  deleteConversation: (id: number | string) => Promise<void>;

  // Loading states
  isConversationsLoading: boolean;
  isConversationLoading: boolean;

  // Error handling
  error: string | null;
  clearError: () => void;
  isConversationsLoadingError: string | null;
  isConversationLoadError: string | null;

  // Message voting
  voteMessage: (messageId: string, vote: MessageVote) => Promise<void>;

  // Thinking state
  thinkingState: string | null;
  interimAnswer: string | null;
}

// Context creation
const AskVantageContext = createContext<AskVantageContextType | undefined>(undefined);

interface AskVantageProviderProps {
  children: ReactNode;
}

// Provider component
export const AskVantageProvider: React.FC<AskVantageProviderProps> = ({ children }) => {
  // State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoSendEnabled, setAutoSendEnabled] = useState(true);

  // Message box interaction callback ref
  const fillMessageBoxRef = useRef<((text: string) => void) | null>(null);

  // Conversation states
  const [isLoadingConvMessages, setIsLoadingConvMessages] = useState<boolean>(false);
  const [isLoadConvMessagesError, setIsLoadConvMessagesError] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [conversationChannel, setConversationChannel] = useState<string | null>(null);

  // Conversation messages states
  const [thinkingState, setThinkingState] = useState<string | null>('');
  const [interimAnswer, setInterimAnswer] = useState<string | null>(null);

  // Conversations states
  const [isLoadingConversations, setIsLoadingConversations] = useState<boolean>(false);
  const [isLoadConversationsError, setIsLoadConversationsError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);

  // Real-time integration (Pusher or Action Cable)
  const { pusherInstance } = usePusher();
  const pusherChannelRef = useRef<any>(null);
  const actionCableConsumer = useRef<Consumer | null>(null);
  const actionCableSubscription = useRef<Subscription | null>(null);
  const currentConvIdRef = useRef<string | null>(null);

  // Check if we should use Pusher or Action Cable
  const usePusherForChat = getEnvValueFor("QUERY_RESULT_PUSH_USE_PUSHER") === "true";

  // Token quota management
  const [quota, setQuota] = useState<{
    display_text: string;
    current_usage: TokenUsageStats;
    loading: boolean;
    error: string | null;
  }>({
    display_text: 'Loading...',
    current_usage: {
      // Balance information
      total_available_tokens: 0,
      remaining_purchased_tokens: 0,

      // Daily free quota statistics
      daily_free_quota: 0,
      daily_free_usage: 0,
      daily_free_remaining: 0,
      daily_free_usage_percent: 0,

      // Total daily consumption
      daily_total_consumed: 0,
      purchased_tokens_used_today: 0,

      // UI state helpers
      quota_status: 'healthy',
      is_quota_exceeded: false,
      is_daily_quota_exhausted: false,
      show_warning: false,

      // Metadata
      daily_quota_resets_at: '',
      plan_type: ''
    },
    loading: true,
    error: null
  });

  const { setDashboard } = useContext(DashboardEventsContext);

  const isConversationsLoading = useMemo(
    () => isLoadingConversations && !conversations.length,
    [isLoadingConversations, conversations]
  );

  const isConversationLoading = useMemo(
    () => isLoadingConvMessages && !messages.length,
    [isLoadingConvMessages, messages]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup Pusher
      if (pusherChannelRef.current && pusherInstance) {
        pusherInstance.unsubscribe(conversationChannel || '');
        pusherChannelRef.current = null;
      }
      // Cleanup Action Cable
      try {
        if (actionCableSubscription.current) {
          // Use unsubscribe() which handles closed connections gracefully
          actionCableSubscription.current.unsubscribe();
          actionCableSubscription.current = null;
        }
      } catch (error) {
        // Silently ignore errors if connection is already closed (expected behavior)
      }

      try {
        if (actionCableConsumer.current) {
          actionCableConsumer.current.disconnect();
          actionCableConsumer.current = null;
        }
      } catch (error) {
        // Silently ignore errors if connection is already closed (expected behavior)
      }
    };
  }, [conversationChannel, pusherInstance]);

  // Load conversations and quota on mount
  useEffect(() => {
    loadConversations();
    refreshQuota();
    setDashboard('ask-vantage');
  }, []);

  // Sidebar controls
  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);

  // Token quota management
  const refreshQuota = useCallback(async () => {
    try {
      setQuota((prev) => ({ ...prev, loading: true, error: null }));
      const tokenData = await assistantApi.getTokenUsageStats();
      setQuota({
        display_text: tokenData.display_text,
        current_usage: tokenData.current_usage,
        loading: false,
        error: null
      });
    } catch (error: any) {
      console.error('Failed to fetch token usage:', error);
      setQuota((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load token usage information'
      }));
    }
  }, []);

  // Use backend-calculated quota exceeded state (no frontend calculation needed)
  const isQuotaExceeded = (quota.current_usage?.is_quota_exceeded || false) && !quota.loading;

  const updateConversation = useCallback(async (convId: string) => {
    try {
      setIsLoadingConvMessages(true);
      setIsLoadConvMessagesError(null);
      setActiveConvId(null);
      currentConvIdRef.current = null;

      const response = await assistantApi.getConversation(convId);

      setActiveConvId(convId);
      currentConvIdRef.current = convId;
      setConversationChannel(`conversation_${convId}`);
      setMessages(sortMessageBlocks(response.messages || []));
    } catch (err: any) {
      console.error('Error loading conversation:', err);
      setIsLoadConvMessagesError(err.response?.data?.error || 'Failed to load conversation');
    } finally {
      setIsLoadingConvMessages(false);
    }
  }, []);

  // Conversation management
  const loadConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    setIsLoadConversationsError(null);
    try {
      const response = await assistantApi.getConversations();
      setConversations(response);
    } catch (err: any) {
      console.error('Error loading conversations:', err);
      setIsLoadConversationsError(err.response?.data?.error || 'Failed to load conversations');
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  // Use custom hook for handling chat events
  const { handleConvChatEvents } = useChatEventHandler({
    setThinkingState,
    setInterimAnswer,
    setMessages,
    setError,
    updateConversation,
    loadConversations,
    refreshQuota,
    currentConvIdRef
  });

  // Real-time subscription management (Pusher or Action Cable)
  useEffect(() => {
    console.log('[AskVantage] Conversation channel:', conversationChannel);
    if (conversationChannel) {
      if (usePusherForChat && pusherInstance) {
        // Use Pusher
        // Unsubscribe from previous channel
        if (pusherChannelRef.current) {
          pusherInstance.unsubscribe(pusherChannelRef.current.name);
        }

        // Subscribe to new channel
        console.log('[AskVantage] Subscribing to pusher channel:', conversationChannel);
        pusherChannelRef.current = pusherInstance.subscribe(conversationChannel);
        // Handle chat update events
        pusherChannelRef.current.bind('chat_update', handleConvChatEvents);

        return () => {
          if (pusherChannelRef.current) {
            pusherInstance.unsubscribe(conversationChannel);
            pusherChannelRef.current = null;
          }
        };
      } else if (!usePusherForChat) {
        // Use Action Cable
        actionCableConsumer.current = createConsumer();

        // Subscribe to conversation channel
        console.log('[AskVantage] Subscribing to Action Cable channel:', conversationChannel);
        actionCableSubscription.current = actionCableConsumer.current.subscriptions.create(
          {
            channel: 'ConversationChannel',
            conversation_id: conversationChannel.replace('conversation_', '') // Extract ID from channel name
          },
          {
            received: handleConvChatEvents
          }
        );

        return () => {
          // Cleanup ActionCable subscription and consumer
          try {
            if (actionCableSubscription.current) {
              // Use unsubscribe() which handles closed connections gracefully
              actionCableSubscription.current.unsubscribe();
              actionCableSubscription.current = null;
            }
          } catch (error) {
            // Silently ignore errors if connection is already closed (expected behavior)
          }

          try {
            if (actionCableConsumer.current) {
              actionCableConsumer.current.disconnect();
              actionCableConsumer.current = null;
            }
          } catch (error) {
            // Silently ignore errors if connection is already closed (expected behavior)
          }
        };
      }
    }
  }, [conversationChannel, usePusherForChat, pusherInstance, handleConvChatEvents]);

  // Subscribe to token balance update events
  useSub(
    'ask_vantage_token_balance_updated',
    useCallback(
      async (data: any) => {
        console.log('[AskVantage] Token balance updated event received:', data);
        // Refresh quota to get updated balance
        await refreshQuota();
        // Refresh the current conversation if exists
        if (currentConvIdRef.current) {
          await updateConversation(currentConvIdRef.current);
        }
      },
      [refreshQuota, updateConversation]
    )
  );

  const createConversation = useCallback(() => {
    setActiveConvId(null);
    currentConvIdRef.current = null;
    setConversationChannel(null);
    setMessages([]);
    setError(null);
    setThinkingState(null);

    // Cleanup previous subscriptions
    if (usePusherForChat && pusherChannelRef.current && pusherInstance) {
      pusherInstance.unsubscribe(conversationChannel || '');
      pusherChannelRef.current = null;
    } else if (!usePusherForChat) {
      try {
        if (actionCableSubscription.current) {
          // Use unsubscribe() which handles closed connections gracefully
          actionCableSubscription.current.unsubscribe();
          actionCableSubscription.current = null;
        }
      } catch (error) {
        // Silently ignore errors if connection is already closed (expected behavior)
      }

      try {
        if (actionCableConsumer.current) {
          actionCableConsumer.current.disconnect();
          actionCableConsumer.current = null;
        }
      } catch (error) {
        // Silently ignore errors if connection is already closed (expected behavior)
      }
    }
  }, [conversationChannel, usePusherForChat, pusherInstance]);

  const selectConversation = useCallback(async (id: string) => {
    console.log('[AskVantage] Selecting conversation:', id);
    try {
      setIsLoadingConvMessages(true);
      setError(null);
      setActiveConvId(null);
      currentConvIdRef.current = null;
      setThinkingState(null);
      setInterimAnswer(null);

      const response = await assistantApi.getConversation(id);

      setActiveConvId(id);
      currentConvIdRef.current = id;
      setConversationChannel(`conversation_${id}`);
      setMessages(sortMessageBlocks(response.messages || []));
      // If the last message is a user message, set the status to processing
    } catch (err: any) {
      console.error('Error loading conversation:', err);
      setError(err.response?.data?.error || 'Failed to load conversation');
    } finally {
      setIsLoadingConvMessages(false);
    }
  }, []);

  // TODO: Pending for implementation
  const renameConversation = useCallback((id: number | string, title: string) => {
    setConversations((prev) => prev.map((conv) => (conv.id === id ? { ...conv, title } : conv)));
  }, []);

  // TODO: Pending for implementation
  const deleteConversation = useCallback(
    async (id: number | string) => {
      try {
        setConversations((prev) => prev.filter((conv) => conv.id !== id));
        if (currentConvIdRef.current === id) {
          createConversation();
        }
      } catch (err) {
        console.error('Error deleting conversation:', err);
        setError('Failed to delete conversation');
      }
    },
    [createConversation]
  );

  // Send message function
  const send = useCallback(
    async (input: string /* opts?: { contextIds?: string[] } */) => {
      setError(null);
      setThinkingState('Thinking...');

      // Append user message immediately to the current conversation
      const userMessage: UserMessage = {
        id: new Date().getTime().toString(),
        message_type: SenderRole.USER,
        content: input,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: RequestStatus.PROCESSING
      };
      setMessages((prev) => [...prev, userMessage]);

      try {
        if (!currentConvIdRef.current) {
          // New conversation
          const response = await assistantApi.createConversation(input);

          setActiveConvId(response.conversation_id);
          currentConvIdRef.current = response.conversation_id;
          setConversationChannel(`conversation_${response.conversation_id}`);

          // Update conversations list
          setConversations((prev) => [
            ...prev,
            {
              id: response.conversation_id,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              title: input || 'New Conversation'
            } as Conversation
          ]);
        } else {
          // Follow-up message to existing conversation
          const response = await assistantApi.continueConversation(currentConvIdRef.current, input);
          if (response.status === RequestStatus.PROCESSING) {
            setThinkingState(response.message);
          }
        }
      } catch (err: any) {
        setThinkingState(null);
        console.error('Send message error:', err);

        // Handle quota exceeded error specifically
        if (err.isQuotaExceeded && err.quota_info) {
          setQuota({
            display_text: err.quota_info.display_text || 'Token limit exceeded',
            current_usage: err.quota_info.current_usage || {
              total_available_tokens: 0,
              remaining_purchased_tokens: 0,
              daily_free_quota: 0,
              daily_free_usage: 0,
              daily_free_remaining: 0,
              daily_free_usage_percent: 100,
              daily_total_consumed: 0,
              purchased_tokens_used_today: 0,
              quota_status: 'exhausted',
              is_quota_exceeded: true,
              is_daily_quota_exhausted: true,
              show_warning: true,
              daily_quota_resets_at: '',
              plan_type: ''
            },
            loading: false,
            error: null
          });
        }

        const errorMsg = err.response?.data?.error || err.message || 'Failed to send message.';
        setError(errorMsg);
      }
    },
    [loadConversations, refreshQuota]
  );

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  // Message voting
  const voteMessage = useCallback(
    async (messageId: string, vote: MessageVote) => {
      try {
        // Determine the vote to send (if same vote, set to neutral)
        const currentMessage = messages.find((msg) => msg.id === messageId) as AssistantMessage;
        const currentVote = currentMessage?.vote;
        const newVote = currentVote === vote ? MessageVote.NEUTRAL : vote;

        await assistantApi.voteMessage(messageId, newVote);

        // Refresh the conversation to get the updated vote state
        if (currentConvIdRef.current) {
          await updateConversation(currentConvIdRef.current);
        }
      } catch (error: any) {
        console.error('Failed to vote on message:', error);
        setError(error.response?.data?.error || 'Failed to vote on message');
      }
    },
    [messages, updateConversation]
  );

  // Auto-send toggle function
  const toggleAutoSend = useCallback(() => {
    setAutoSendEnabled((prev) => !prev);
  }, []);

  // Fill message box function
  const fillMessageBox = useCallback((text: string) => {
    if (fillMessageBoxRef.current) {
      fillMessageBoxRef.current(text);
    }
  }, []);

  // Register function for message box to call
  const registerFillMessageBox = useCallback((fn: (text: string) => void) => {
    fillMessageBoxRef.current = fn;
  }, []);

  const contextValue: AskVantageContextType = {
    sidebarOpen,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    messages,
    send,
    fillMessageBox,
    registerFillMessageBox,
    autoSendEnabled,
    toggleAutoSend,
    quota,
    refreshQuota,
    isQuotaExceeded,
    kpiBarVisible: true, // This could be configurable
    conversations,
    activeConversationId: activeConvId,
    createConversation,
    selectConversation,
    renameConversation,
    deleteConversation,
    isConversationsLoading,
    isConversationLoading,
    error,
    clearError,
    isConversationsLoadingError: isLoadConversationsError,
    isConversationLoadError: isLoadConvMessagesError,
    voteMessage,
    thinkingState,
    interimAnswer
  };

  return <AskVantageContext.Provider value={contextValue}>{children}</AskVantageContext.Provider>;
};

// Hook to use context
export const useAskVantage = (): AskVantageContextType => {
  const context = useContext(AskVantageContext);
  if (context === undefined) {
    throw new Error('useAskVantage must be used within an AskVantageProvider');
  }
  return context;
};
